import styled from 'styled-components'

import { media } from 'styles/media'

export interface FooterLinksRegion {
  name: string
  url: string
}

export interface FooterLinksState {
  name: string
  url: string
  regions: FooterLinksRegion[]
}

export interface FooterLinksCountry {
  name: string
  url: string
  states: FooterLinksState[]
}

export interface FooterLinksSubcategory {
  name: string
  url: string
}

export interface FooterLinksAdventureType {
  name: string
  url: string
}

export interface FooterLinksTree {
  countries: FooterLinksCountry[]
  subcategories: FooterLinksSubcategory[]
  adventureTypes: Record<string, FooterLinksAdventureType[]>
}

const ADVENTURE_CATEGORY_LABELS: Record<string, string> = {
  activity: 'Activities',
  nature_feature: 'Nature',
  food_drink: 'Food & drink',
  other: 'Shops and culture',
}

const ADVENTURE_CATEGORY_ORDER = [
  'activity',
  'nature_feature',
  'food_drink',
  'other',
]

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const Section = styled.section`
  margin: 0;
`

const SectionHeading = styled.div`
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(233, 239, 233, 0.9);
  margin-bottom: 24px;
  font-weight: 600;
`

const CountryBlock = styled.div`
  margin-bottom: 56px;
`

const CountryHeading = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 14px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(233, 239, 233, 0.18);

  a {
    color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme?.['-ds-neutral-300']?.value};
    }
  }
`

const StateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 8px 0 14px;

  ${media.md`
    grid-template-columns: 140px 1fr;
    gap: 24px;
    padding: 10px 0;
    align-items: baseline;
  `}
`

const StateName = styled.div`
  font-size: 13px;
  font-weight: 400;

  a {
    color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme?.['-ds-neutral-300']?.value};
    }
  }
`

const RegionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 14px;

  ${media.md`
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px 18px;
  `}

  a {
    font-size: 13px;
    color: rgba(233, 239, 233, 0.9);
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    }
  }
`

const ChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Chip = styled.a`
  font-size: 12.5px;
  padding: 5px 12px;
  border: 1px solid rgba(233, 239, 233, 0.3);
  border-radius: 999px;
  color: rgba(233, 239, 233, 0.9);
  text-decoration: none;
  background: transparent;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  &:hover {
    background: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    color: ${({ theme }) => theme.brand.jungle};
    border-color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    text-decoration: none;
  }
`

const AdventureGroup = styled.div`
  margin-bottom: 24px;
`

const AdventureGroupTitle = styled.div`
  font-size: 14px;
  color: rgba(233, 239, 233, 0.9);
  margin-bottom: 12px;
  font-weight: 500;
`

interface ExploreLinksProps {
  tree: FooterLinksTree
}

const ExploreLinks = ({ tree }: ExploreLinksProps) => (
  <MainCol>
    <Section>
      <SectionHeading>Explore by destination</SectionHeading>
      {tree.countries.map((country) => (
        <CountryBlock key={country.name}>
          <CountryHeading>
            <a href={country.url}>{country.name}</a>
          </CountryHeading>
          {country.states.map((state) => (
            <StateRow key={state.name}>
              <StateName>
                <a href={state.url}>{state.name}</a>
              </StateName>
              {state.regions.length > 0 && (
                <RegionGrid>
                  {state.regions.map((region) => (
                    <a key={region.url} href={region.url}>
                      {region.name}
                    </a>
                  ))}
                </RegionGrid>
              )}
            </StateRow>
          ))}
        </CountryBlock>
      ))}
    </Section>

    {tree.subcategories.length > 0 && (
      <Section>
        <SectionHeading>Stay types</SectionHeading>
        <ChipWrap>
          {tree.subcategories.map((subcategory) => (
            <Chip key={subcategory.url} href={subcategory.url}>
              {subcategory.name}
            </Chip>
          ))}
        </ChipWrap>
      </Section>
    )}

    {ADVENTURE_CATEGORY_ORDER.some((c) => tree.adventureTypes[c]?.length) && (
      <Section>
        <SectionHeading>Adventure types</SectionHeading>
        {ADVENTURE_CATEGORY_ORDER.map((category) => {
          const items = tree.adventureTypes[category]
          if (!items?.length) return null
          return (
            <AdventureGroup key={category}>
              <AdventureGroupTitle>
                {ADVENTURE_CATEGORY_LABELS[category]}
              </AdventureGroupTitle>
              <ChipWrap>
                {items.map((item) => (
                  <Chip key={item.url} href={item.url}>
                    {item.name}
                  </Chip>
                ))}
              </ChipWrap>
            </AdventureGroup>
          )
        })}
      </Section>
    )}
  </MainCol>
)

export default ExploreLinks
